import { BigDotIcon, Grid2, Typography } from "components";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { EquityBasketListInterface } from "interfaces/EquityBasket";
import { OrderSimulationForm } from "interfaces/OrderSimulation";
import { useForm, useWatch } from "react-hook-form";
import { useAppSelector } from "store/store";
import axiosInstance from "utils/axiosInstance";

export const ReturnBasketTitle = ({ basketName }: { basketName: string }) => {
  return (
    <Grid2 container spacing={1} display={"flex"} alignItems={"center"}>
      <Grid2>
        <Typography fontSize={17} fontWeight={700}>
          Generate Trades
        </Typography>
      </Grid2>
      <Grid2>
        <Typography>
          <BigDotIcon color="#9AA4B2" />
        </Typography>
      </Grid2>
      <Grid2>
        <Typography fontSize={12} fontWeight={500} color="#697586">
          {basketName}
        </Typography>
      </Grid2>
    </Grid2>
  );
};

export const useOrderSimulation = ({
  modelData,
}: {
  modelData: EquityBasketListInterface;
}) => {
  const userData = useAppSelector((state) => state?.user?.userData);
  const companies = useAppSelector((state) => state?.app?.companies);

  const defaultValues: OrderSimulationForm = {
    allocation: {
      allocType: "",
      allocationBy: "",
      useCurrentAllocation: null,
      rebalanceUsing: "",
      weightageDistributionMethod: "",
      portfolios: [],
      companies: [],
      allocVersion: null,
      exitWeightage: "",
      stockWise: false,
      frozenCompanies: [],
      holdCompanies: [],
      buyCompanies: [],
      sellCompanies: [],
      exitParameter: "",
      buyParameter: "",
    },
    extraThings: {
      rightVisibleTable: null,
    },
    portfolioSelectionFilter: {
      portfolio_group: [],
      portfolio_value: [],
      available_cash: [],
    },
    generateTradeResponse: [],
    customQuantities: [],
    generateOrderSheet: {
      rationale: "",
      limitOrder: false,
    },
    generateResponsePayload: {},
  };

  const form = useForm<OrderSimulationForm>({ defaultValues });

  const watchAllocationType = useWatch({
    control: form.control,
    name: "allocation.allocType",
  });
  const watchAllocationBy = useWatch({
    control: form.control,
    name: "allocation.allocationBy",
  });
  const watchRightVisibleTable = useWatch({
    control: form.control,
    name: "extraThings.rightVisibleTable",
  });
  const watchPortfolioFilter = useWatch({
    control: form.control,
    name: "portfolioSelectionFilter",
  });

  const {
    data: allocationVersionList,
    isFetching: allocationVersionListLoading,
  } = useFetchQuery({
    key: ["MODEL_PORTFOLIO_ALLOCATION_VERSION_LIST"],
    route: `/model_portfolio/${modelData?.id}/allocation-versions/`,
    enabled: !!modelData?.id,
  });

  const { data: modelPortfolioList, isFetching: modelPortfolioFetching } =
    useFetchQuery({
      key: ["MODEL_PORTFOLIO_LIST", watchPortfolioFilter],
      route: `/model_portfolio/${modelData?.id}/allocation/`,
      enabled: !!modelData?.id,
      requestBody: {
        portfolio_group: JSON.stringify(watchPortfolioFilter.portfolio_group),
        portfolio_value: JSON.stringify(watchPortfolioFilter.portfolio_value),
        available_cash: JSON.stringify(watchPortfolioFilter.available_cash),
      },
      success: () => form.setValue("allocation.portfolios", []),
    });

  const { mutate: generateTrade, isLoading } = useMutation({
    mutationKey: ["GENERATE_TRADE"],
    mutationFn: (data) =>
      axiosInstance.post(`/model_portfolio/${modelData?.id}/allocation/`, data),
    onSuccess: (response: any) => {
      form.setValue("extraThings.rightVisibleTable", "GENERATE_TRADE_TABLE");
      form.setValue("generateTradeResponse", response.data);
    },
  });

  const ALLOCATION_TYPE = [
    { name: "Allocation", value: "allocation" },
    { name: "Rebalance", value: "rebalance" },
    { name: "Exit", value: "exit" },
  ];

  const ALLOCATION_BY = [
    { name: "Model", value: "version" },
    { name: "Company", value: "company" },
    { name: "Customized", value: "customized" },
  ];

  const TRANSACTION_TYPE = [
    { name: "Allocated", value: "allocated" },
    { name: "Current", value: "current" },
  ];

  const REFERENCE_TYPE_FOR_HEADER_CARD = [
    { name: "Current Value", value: "current" },
    { name: "Portfolio Value", value: "portfolio" },
    { name: "Portfolio Value - Absolute", value: "portfolioAbsolute" },
    { name: "Current Value - Absolute", value: "currentAbsolute" },
    { name: "Committed Value", value: "committedValue" },
    { name: "Committed Value - Absolute", value: "commitedValueAbsolute" },
    {
      name: userData?.useCeAmount ? "Cash & Cash Equivalent" : "Ledger Balance",
      value: "ledgerBalance",
    },
  ];

  const REFERENCE_TYPE = [
    {
      name: "Current Value",
      value: "current",
      allocationType: ["rebalance", "allocation", "exit"],
      rebalance: ["company", "version", "customized"],
      allocation: ["company"],
      exit: ["companyWeightage"],
    },
    {
      name: "Portfolio Value",
      value: "portfolio",
      allocationType: ["rebalance", "allocation", "exit"],
      rebalance: ["company", "version", "customized"],
      allocation: ["company"],
      exit: ["companyWeightage"],
    },
    {
      name: "Portfolio Value - Absolute",
      value: "portfolioAbsolute",
      allocationType: ["allocation"],
      rebalance: [] as string[],
      allocation: ["company"],
    },
    {
      name: "Current Value - Absolute",
      value: "currentAbsolute ",
      allocationType: ["allocation"],
      rebalance: [],
      allocation: ["company"],
    },
    {
      name: "Committed Value",
      value: "committedValue",
      allocationType: ["rebalance", "allocation", "exit"],
      rebalance: ["company", "version"],
      allocation: ["company", "version"],
      exit: ["companyWeightage"],
    },
    {
      name: "Committed Value - Absolute",
      value: "commitedValueAbsolute",
      allocationType: ["allocation"],
      rebalance: [],
      allocation: ["version", "company"],
    },
    {
      name: userData?.useCeAmount ? "Cash & Cash Equivalent" : "Ledger Balance",
      value: "ledgerBalance",
      allocationType: ["allocation"],
      rebalance: [],
      allocation: ["company", "version"],
    },
  ];

  const WEIGHTAGE_DISTRIBUTION_METHOD = [
    { value: "PRORATA", name: "Prorata" },
    { value: "FIXED", name: "No Redistribution" },
  ];

  const ALLOCATION_BY_EXIT = [
    { name: "Model", disabled: false, value: "version" },
    { name: "Portfolio", disabled: false, value: "portfolio" },
    { name: "Company-Absolute", disabled: false, value: "company" },
    { name: "Company-Weightage", disabled: false, value: "companyWeightage" },
  ];

  const CUSTOMIZED_PARAMETER = [
    { value: "companyAbsolute", name: "Company-Absolute" },
    { value: "companyFinal", name: "Company-Final Weightage" },
    { value: "weightageWise", name: "Weightage of CV/PV" },
    { value: "sellAmountAllocation", name: "Sell Amount-Allocation" },
  ];

  const returnExitByTitle = (key: string) => {
    switch (key) {
      case "portfolio":
        return { title: "Exit by Portfolio", navigate: "EDIT_PORTFOLIO" };
      case "version":
        return { title: "Exit by Model", navigate: "SELECT_PORTFOLIO" };
      case "companyWeightage":
        return {
          title: "Exit by Company Weightage",
          navigate: "EDIT_COMPANY",
        };
      default:
        return { title: "Exit by Company", navigate: "EDIT_COMPANY" };
    }
  };

  const matchObjectsStrict = <T extends Record<string, any>>(
    obj1: T,
    obj2: T,
  ): boolean => {
    if (obj1 === obj2) return true;

    if (!obj1 || !obj2) return false;

    const keys1 = Object.keys(obj1) as (keyof T)[];
    const keys2 = Object.keys(obj2) as (keyof T)[];

    if (keys1.length !== keys2.length) return false;

    return keys1.every((key) => {
      if (!(key in obj2)) return false;

      const val1 = obj1[key];
      const val2 = obj2[key];

      if (Array.isArray(val1) && Array.isArray(val2)) {
        if (val1.length !== val2.length) return false;
        return val1.every((item: any, idx: number) =>
          typeof item === "object" &&
          item !== null &&
          typeof val2[idx] === "object" &&
          val2[idx] !== null
            ? matchObjectsStrict(item, val2[idx])
            : item === val2[idx],
        );
      }

      if (
        typeof val1 === "object" &&
        val1 !== null &&
        typeof val2 === "object" &&
        val2 !== null
      ) {
        return matchObjectsStrict(val1, val2);
      }

      return val1 === val2;
    });
  };

  return {
    ALLOCATION_TYPE,
    ALLOCATION_BY,
    TRANSACTION_TYPE,
    REFERENCE_TYPE,
    WEIGHTAGE_DISTRIBUTION_METHOD,
    allocationVersionList,
    allocationVersionListLoading,
    modelPortfolioList,
    modelPortfolioFetching,
    userData,
    form,
    generateTrade,
    ALLOCATION_BY_EXIT,
    returnExitByTitle,
    companies,
    isLoading,
    watchAllocationType,
    watchAllocationBy,
    watchRightVisibleTable,
    REFERENCE_TYPE_FOR_HEADER_CARD,
    CUSTOMIZED_PARAMETER,
    matchObjectsStrict,
  };
};
